/**
 * train-analytics-page.tsx — Phân tích một nhân viên AI (/ai/nhan-vien/:botId/phan-tich), kiểu HelpKnow:
 *   • Tổng quan        — theo khoảng ngày: lượt khách AI xử lý, AI đã trả lời, hội thoại, khách,
 *                        % chuyển người thật, tỷ lệ tự xử lý, thời gian trả lời; biểu đồ theo ngày.
 *   • Cuộc trò chuyện  — lọc chuyển người / tự xử lý; xuất CSV.
 *   • Tin nhắn         — câu hỏi khách, câu trả lời, trạng thái, căn cứ trả lời (nguồn + % liên quan),
 *                        ngưỡng đã dùng; lọc trạng thái + tìm; xuất CSV.
 * API: train-analytics-routes.ts.
 */
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, BarChart3, Download, Loader2, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { openInNewTab } from '@/components/ai/open-in-new-tab'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import { currentLocale } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import {
  useAnalyticsOverview, useAnalyticsMessages, useAnalyticsConversations,
  fetchAnalyticsMessages, fetchAnalyticsConversations,
  type AnalyticsOverview, type AnalyticsRange,
} from '@/hooks/use-ai-train-test'
import { ANALYTICS_STATUS_LABELS, basisLine, downloadText, lastNDays, toCsv } from './train-lib'

const PAGE = 50
const fmtTime = (s: string) => new Date(s).toLocaleString(currentLocale(), { dateStyle: 'short', timeStyle: 'short' })
const pct = (v: number | null) => (v == null ? '—' : `${v.toLocaleString(currentLocale())}%`)

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'secondary' | 'destructive'> = {
  answered: 'success', handoff: 'warning', skipped: 'secondary', error: 'destructive',
}

export function TrainAnalyticsPage() {
  const { t } = useTranslation('aiTrainUi')
  const { botId } = useParams<{ botId: string }>()
  const navigate = useNavigate()
  const [range, setRange] = useState<AnalyticsRange>(() => lastNDays(7))
  const overview = useAnalyticsOverview(botId, range)

  return (
    <div className="mx-auto max-w-7xl space-y-4 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/ai/nhan-vien/${botId}`)}><ArrowLeft className="h-4 w-4" /> {t('common:actions.back')}</Button>
        <h1 className="flex items-center gap-2 text-lg font-semibold">
          <BarChart3 className="h-5 w-5 text-primary" /> {t('analytics.title')}{overview.data?.botName ? ` · ${overview.data.botName}` : ''}
        </h1>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          {[7, 30, 90].map((n) => (
            <Button key={n} variant="outline" size="sm" onClick={() => setRange(lastNDays(n))}>{t('analytics.days', { count: n })}</Button>
          ))}
          <Input type="date" className="h-8 w-36" value={range.from} max={range.to} onChange={(e) => e.target.value && setRange((r) => ({ ...r, from: e.target.value }))} />
          <span className="text-muted-foreground">–</span>
          <Input type="date" className="h-8 w-36" value={range.to} min={range.from} onChange={(e) => e.target.value && setRange((r) => ({ ...r, to: e.target.value }))} />
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">{t('analytics.tabOverview')}</TabsTrigger>
          <TabsTrigger value="conversations">{t('analytics.tabConversations')}</TabsTrigger>
          <TabsTrigger value="messages">{t('analytics.tabMessages')}</TabsTrigger>
        </TabsList>
        <TabsContent value="overview">
          {overview.isLoading ? <Loading label={t('analytics.aggregating')} />
            : overview.isError ? <ErrorState message={apiError(overview.error)} />
            : overview.data ? <Overview data={overview.data} /> : null}
        </TabsContent>
        <TabsContent value="conversations">{botId && <Conversations botId={botId} range={range} />}</TabsContent>
        <TabsContent value="messages">{botId && <Messages botId={botId} range={range} />}</TabsContent>
      </Tabs>
    </div>
  )
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border bg-background p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
      {hint && <p className="mt-0.5 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  )
}

function Overview({ data }: { data: AnalyticsOverview }) {
  const { t } = useTranslation('aiTrainUi')
  const max = Math.max(1, ...data.daily.map((d) => d.turns))
  const n = (v: number) => v.toLocaleString(currentLocale())
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label={t('analytics.statTurns')} value={n(data.turns)} hint={t('analytics.statReplies', { count: data.replies, num: n(data.replies) })} />
        <Stat label={t('analytics.statConversations')} value={n(data.conversations)} />
        <Stat label={t('analytics.statCustomers')} value={n(data.customers)} />
        <Stat label={t('analytics.statLatency')} value={data.avgLatencyMs == null ? '—' : t('analytics.seconds', { value: (data.avgLatencyMs / 1000).toFixed(1) })} />
        <Stat label={t('analytics.statHandoffRate')} value={pct(data.handoffRate)} hint={t('analytics.statHandoffConvs', { count: data.handoffConversations, num: n(data.handoffConversations) })} />
        <Stat label={t('analytics.statResolvedRate')} value={pct(data.resolvedRate)} hint={t('analytics.statResolvedHint')} />
        <Stat label={t('analytics.statSkipped')} value={n(data.skipped)} hint={t('analytics.statSkippedHint')} />
        <Stat label={t('common:status.error')} value={n(data.errors)} />
      </div>
      <div className="rounded-lg border bg-background p-3">
        <p className="mb-2 text-sm font-medium">{t('analytics.chartTitle')}</p>
        {data.turns === 0 ? (
          <p className="py-8 text-center text-xs text-muted-foreground">{t('analytics.noData')}</p>
        ) : (
          <>
            <div className="flex h-40 items-end gap-0.5" role="img" aria-label={t('analytics.chartAria')}>
              {data.daily.map((d) => (
                <div key={d.day} className="group relative flex h-full min-w-0 flex-1 items-end">
                  <div
                    className="w-full rounded-t-[4px] bg-primary/80 group-hover:bg-primary"
                    style={{ height: `${Math.max(d.turns > 0 ? 2 : 0, (d.turns / max) * 100)}%` }}
                  />
                  <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 hidden -translate-x-1/2 whitespace-nowrap rounded border bg-popover px-2 py-1 text-[11px] text-popover-foreground shadow group-hover:block">
                    {t('analytics.dayTooltip', { day: d.day, turns: d.turns, replies: d.replies, handoffs: d.handoffs })}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-1 flex justify-between text-[11px] text-muted-foreground">
              <span>{data.daily[0]?.day}</span><span>{data.daily[data.daily.length - 1]?.day}</span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function Pager({ total, offset, onChange }: { total: number; offset: number; onChange: (o: number) => void }) {
  const { t } = useTranslation('aiTrainUi')
  if (total <= PAGE) return null
  return (
    <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground">
      {offset + 1}–{Math.min(offset + PAGE, total)} / {total}
      <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => onChange(Math.max(0, offset - PAGE))}>{t('common:actions.previous')}</Button>
      <Button variant="outline" size="sm" disabled={offset + PAGE >= total} onClick={() => onChange(offset + PAGE)}>{t('analytics.pagerNext')}</Button>
    </div>
  )
}

function Messages({ botId, range }: { botId: string; range: AnalyticsRange }) {
  const { t } = useTranslation('aiTrainUi')
  const [status, setStatus] = useState('all')
  const [search, setSearch] = useState('')
  const [applied, setApplied] = useState('')
  const [offset, setOffset] = useState(0)
  const [exporting, setExporting] = useState(false)
  const params = useMemo(() => ({
    ...range, ...(status !== 'all' ? { status } : {}), ...(applied ? { search: applied } : {}), limit: PAGE, offset,
  }), [range, status, applied, offset])
  const q = useAnalyticsMessages(botId, params)

  async function exportCsv() {
    setExporting(true)
    try {
      const r = await fetchAnalyticsMessages(botId, { ...params, limit: 1_000, offset: 0 })
      downloadText(toCsv([
        [t('analytics.csvTime'), t('analytics.csvCustomer'), t('analytics.csvChannel'), t('analytics.csvQuestion'), t('analytics.csvAnswer'), t('analytics.csvStatus'), t('analytics.csvBasis'), t('analytics.csvThreshold'), t('analytics.csvHandoffReason')],
        ...r.items.map((m) => [
          fmtTime(m.at), m.customer ?? '', m.channel ?? '', m.question ?? '', m.answer ?? '', ANALYTICS_STATUS_LABELS[m.status],
          basisLine(m.basis), m.threshold != null ? `${Math.round(m.threshold * 100)}%` : '', m.handoffReason ?? '',
        ]),
      ]), `tin-nhan-ai-${range.from}-${range.to}.csv`)
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={status} onValueChange={(v) => { setStatus(v); setOffset(0) }}>
          <SelectTrigger className="h-8 w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('analytics.allStatuses')}</SelectItem>
            {Object.entries(ANALYTICS_STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-2 h-4 w-4 text-muted-foreground" />
          <Input
            className="h-8 pl-8" placeholder={t('analytics.searchPlaceholder')} value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setApplied(search.trim()); setOffset(0) } }}
          />
        </div>
        <Button variant="outline" size="sm" className="ml-auto" disabled={exporting} onClick={() => void exportCsv()}>
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} {t('analytics.exportCsv')}
        </Button>
      </div>
      {q.isLoading ? <Loading label={t('analytics.loadingMessages')} /> : q.isError ? <ErrorState message={apiError(q.error)} /> : (
        <>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full min-w-[900px] text-sm">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">{t('analytics.colTime')}</th>
                  <th className="px-3 py-2">{t('analytics.colQuestion')}</th>
                  <th className="px-3 py-2">{t('analytics.colAnswer')}</th>
                  <th className="px-3 py-2">{t('analytics.colStatus')}</th>
                  <th className="px-3 py-2">{t('analytics.colBasis')}</th>
                </tr>
              </thead>
              <tbody>
                {(q.data?.items ?? []).length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-xs text-muted-foreground">{t('analytics.noMessages')}</td></tr>
                )}
                {(q.data?.items ?? []).map((m) => (
                  <tr key={m.runId} className="border-t align-top">
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-muted-foreground">
                      {fmtTime(m.at)}<br />{m.customer ?? ''}{m.channel ? ` · ${m.channel}` : ''}
                    </td>
                    <td className="max-w-[260px] px-3 py-2"><p className="line-clamp-4 whitespace-pre-wrap">{m.question ?? '—'}</p></td>
                    <td className="max-w-[320px] px-3 py-2">
                      <p className="line-clamp-4 whitespace-pre-wrap">{m.answer ?? (m.handoffReason ? `↪ ${m.handoffReason}` : '—')}</p>
                      {m.answer && !m.answerSent && <p className="text-[11px] text-muted-foreground">{t('analytics.suggestedForStaff')}</p>}
                    </td>
                    <td className="px-3 py-2"><Badge variant={STATUS_VARIANT[m.status]}>{ANALYTICS_STATUS_LABELS[m.status]}</Badge></td>
                    <td className="max-w-[260px] px-3 py-2 text-xs">
                      {m.basis.length ? (
                        <ul className="space-y-0.5">
                          {m.basis.map((b, i) => (
                            <li key={i} className={cn('truncate', b.score != null && m.threshold != null && b.score < m.threshold && 'text-muted-foreground')}>
                              {b.title}{b.score != null ? ` · ${Math.round(b.score * 100)}%` : ''}
                            </li>
                          ))}
                        </ul>
                      ) : <span className="text-muted-foreground">{t('analytics.noLookup')}</span>}
                      {m.threshold != null && <p className="mt-0.5 text-[11px] text-muted-foreground">{t('analytics.threshold', { pct: Math.round(m.threshold * 100) })}</p>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager total={q.data?.total ?? 0} offset={offset} onChange={setOffset} />
          <p className="text-[11px] text-muted-foreground">{t('analytics.basisNote')}</p>
        </>
      )}
    </div>
  )
}

function Conversations({ botId, range }: { botId: string; range: AnalyticsRange }) {
  const { t } = useTranslation('aiTrainUi')
  const [filter, setFilter] = useState('all')
  const [offset, setOffset] = useState(0)
  const [exporting, setExporting] = useState(false)
  const params = useMemo(() => ({
    ...range, limit: PAGE, offset,
    ...(filter === 'handoff' ? { handoff: 'yes' } : filter === 'resolved' ? { resolved: 'yes' } : filter === 'unresolved' ? { resolved: 'no' } : {}),
  }), [range, filter, offset])
  const q = useAnalyticsConversations(botId, params)

  async function exportCsv() {
    setExporting(true)
    try {
      const r = await fetchAnalyticsConversations(botId, { ...params, limit: 1_000, offset: 0 })
      downloadText(toCsv([
        [t('analytics.csvCustomer'), t('analytics.csvChannel'), t('analytics.csvStarted'), t('analytics.csvLatest'), t('analytics.csvTurns'), t('analytics.csvAiReplies'), t('analytics.colHandoff'), t('analytics.resolved')],
        ...r.items.map((c) => [c.customer ?? '', c.channel ?? '', fmtTime(c.firstAt), fmtTime(c.lastAt), c.turns, c.replies, c.handoff ? t('common:misc.yes') : t('common:misc.no'), c.resolved ? t('common:misc.yes') : t('common:misc.no')]),
      ]), `cuoc-tro-chuyen-ai-${range.from}-${range.to}.csv`)
    } catch (err) {
      toast.error(apiError(err))
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={filter} onValueChange={(v) => { setFilter(v); setOffset(0) }}>
          <SelectTrigger className="h-8 w-56"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('analytics.allConversations')}</SelectItem>
            <SelectItem value="resolved">{t('analytics.resolved')}</SelectItem>
            <SelectItem value="unresolved">{t('analytics.unresolved')}</SelectItem>
            <SelectItem value="handoff">{t('analytics.handedOff')}</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" className="ml-auto" disabled={exporting} onClick={() => void exportCsv()}>
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} {t('analytics.exportCsv')}
        </Button>
      </div>
      {q.isLoading ? <Loading label={t('analytics.loading')} /> : q.isError ? <ErrorState message={apiError(q.error)} /> : (
        <>
          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">{t('analytics.csvCustomer')}</th>
                  <th className="px-3 py-2">{t('analytics.csvChannel')}</th>
                  <th className="px-3 py-2">{t('analytics.colTime')}</th>
                  <th className="px-3 py-2 text-right">{t('analytics.colTurns')}</th>
                  <th className="px-3 py-2">{t('analytics.colResult')}</th>
                </tr>
              </thead>
              <tbody>
                {(q.data?.items ?? []).length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-xs text-muted-foreground">{t('analytics.noConversations')}</td></tr>
                )}
                {(q.data?.items ?? []).map((c) => (
                  <tr key={c.conversationId} className="cursor-pointer border-t hover:bg-muted/40" title={t('analytics.openInNewTab')} onClick={() => openInNewTab(`/conversations/${c.conversationId}`)}>
                    <td className="px-3 py-2">{c.customer ?? '—'}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{c.channel ?? '—'}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{fmtTime(c.firstAt)} → {fmtTime(c.lastAt)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{c.turns} / {c.replies}</td>
                    <td className="px-3 py-2">
                      {c.handoff ? <Badge variant="warning">{t('analytics.colHandoff')}</Badge>
                        : c.resolved ? <Badge variant="success">{t('analytics.resolved')}</Badge>
                        : <Badge variant="secondary">{t('analytics.notReplied')}</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager total={q.data?.total ?? 0} offset={offset} onChange={setOffset} />
        </>
      )}
    </div>
  )
}
